import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, MoreVertical, Building2, Users, Activity, Target,
  DollarSign, Clock, X, ArrowUpDown, Filter
} from 'lucide-react';
import { Client, NewClient } from '../../../types';
import { useClients, useModal, useFilters } from '../../../hooks';
import { ClientForm } from '../ClientForm/ClientForm';
import { Button, Badge, Card, EmptyState, LoadingSpinner, PageHeader } from '../../common';

export const ClientsList: React.FC = () => {
  const navigate = useNavigate();
  const { clients, loading, createClient, refetch } = useClients();
  const { isOpen: showNewClientForm, openModal: openCreateForm, closeModal: closeCreateForm } = useModal();
  const [creating, setCreating] = useState(false);
  const [sortColumn, setSortColumn] = useState<'name' | 'value'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const { filteredItems: filteredClients, filters, updateFilter } = useFilters(
    clients,
    (client, { searchTerm = '', statusFilter = 'all' }) => {
      if (!client || !client.name || !client.email) return false;

      const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                     client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                     (client.industry ? client.industry.toLowerCase().includes(searchTerm.toLowerCase()) : false);
      const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
      return matchesSearch && matchesStatus;
    },
    { searchTerm: '', statusFilter: 'all' }
  );

  const getIndustryIcon = (industry: string) => {
    const colors: { [key: string]: string } = {
      'Technology': 'text-blue-600 bg-blue-50',
      'Finance': 'text-emerald-600 bg-emerald-50',
      'Healthcare': 'text-rose-600 bg-rose-50',
      'Retail': 'text-purple-600 bg-purple-50',
      'Manufacturing': 'text-orange-600 bg-orange-50',
      'Consulting': 'text-indigo-600 bg-indigo-50',
      'No Industry': 'text-gray-600 bg-gray-50'
    };
    return colors[industry] || 'text-gray-600 bg-gray-50';
  };

  const formatValue = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value}`;
  };

  const handleSort = (column: 'name' | 'value') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    prospects: clients.filter(c => c.status === 'prospective').length,
    totalValue: clients.length * 50000,
    avgDealSize: clients.length > 0 ? 50000 : 0
  };

  const sortedAndFilteredClients = [...filteredClients].sort((a, b) => {
    let aVal: string | number = sortColumn === 'name' ? a.name : 50000;
    let bVal: string | number = sortColumn === 'name' ? b.name : 50000;

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const handleCreateClient = async (clientData: NewClient) => {
    try {
      setCreating(true);
      await createClient(clientData);
      closeCreateForm();
    } catch (error) {
      console.error('Failed to create client:', error);
      alert('Failed to create client. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoadingSpinner message="Loading clients..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Clients"
        subtitle="Manage and track your client relationships"
        actions={
          <Button
            variant="primary"
            size="md"
            icon={<Plus className="w-4 h-4" />}
            onClick={openCreateForm}
          >
            New Client
          </Button>
        }
      />

      <div className="px-6 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card variant="grayCompact">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Total Clients</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{stats.total}</p>
              </div>
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card variant="grayCompact">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Active</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{stats.active}</p>
              </div>
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Activity className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
          </Card>

          <Card variant="grayCompact">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Pipeline Value</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{formatValue(stats.totalValue)}</p>
              </div>
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card variant="grayCompact">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">Avg Deal Size</p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">{formatValue(stats.avgDealSize)}</p>
              </div>
              <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-amber-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search clients, contacts, or industries..."
              value={filters.searchTerm}
              onChange={(e) => updateFilter('searchTerm', e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            {filters.searchTerm && (
              <button
                onClick={() => updateFilter('searchTerm', '')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <select
            value={filters.statusFilter}
            onChange={(e) => updateFilter('statusFilter', e.target.value)}
            className="text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="prospective">Prospect</option>
          </select>

          <Button
            variant="secondary"
            size="sm"
            icon={<Filter className="w-4 h-4" />}
          >
            Filters
          </Button>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mt-6">
          {sortedAndFilteredClients.length > 0 ? (
            <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
              <tr>
                <th className="text-left py-3 px-6">
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 uppercase tracking-wider hover:text-gray-900"
                  >
                    Client
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Industry</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-6">
                  <button
                    onClick={() => handleSort('value')}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-600 uppercase tracking-wider hover:text-gray-900"
                  >
                    Value
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Deals</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-600 uppercase tracking-wider">Last Contact</th>
                <th className="text-left py-3 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredClients.map((client) => (
                <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors group cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)}>
                  <td className="py-3 px-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${getIndustryIcon(client.industry || 'No Industry')}`}>
                        <span className="text-sm font-semibold">{client.name.substring(0, 2).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{client.name}</p>
                        <p className="text-xs text-gray-500">ID: #{client.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-6">
                    <div>
                      <p className="text-sm text-gray-900">{client.email}</p>
                      {client.phone && <p className="text-xs text-gray-500 mt-0.5">{client.phone}</p>}
                    </div>
                  </td>
                  <td className="py-3 px-6">
                    <span className="text-sm text-gray-700">{client.industry || 'No Industry'}</span>
                  </td>
                  <td className="py-3 px-6">
                    <Badge status={client.status} />
                  </td>
                  <td className="py-3 px-6">
                    <p className="font-medium text-gray-900">{formatValue(50000)}</p>
                  </td>
                  <td className="py-3 px-6">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">2</span>
                      <div className="flex gap-0.5">
                        {[...Array(Math.min(2, 5))].map((_, i) => (
                          <div key={i} className="w-1 h-3 bg-blue-200 rounded-full"></div>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-6">
                    <div className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(client.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="py-3 px-6">
                    <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-100 rounded transition-all" onClick={(e) => e.stopPropagation()}>
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            icon={Users}
            title="No clients found"
            description={
              filters.searchTerm || filters.statusFilter !== 'all'
                ? 'Try adjusting your search or filter.'
                : 'Get started by creating your first client.'
            }
            action={
              !filters.searchTerm && filters.statusFilter === 'all' ? (
                <Button
                  variant="primary"
                  size="md"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={openCreateForm}
                >
                  Add Client
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </div>

    {showNewClientForm && (
        <ClientForm
          onClose={() => closeCreateForm()}
          onSubmit={handleCreateClient}
          loading={creating}
        />
      )}
    </div>
  );
};
